import {Component, EventEmitter, Inject, OnInit, Output, ViewEncapsulation} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FinishReadingDialogEnum} from '@core/enums/finish-reading-dialog.enum';
import {TtsControlEnum} from '@core/enums/tts-control.enum';
import {ReadingSpeedEnum} from '@core/enums/reading-speed.enum';
import {getLanguageEnumByLanguageCode} from '@core/enums/language.enum';
import {ReadingContentTypeEnum} from '@core/enums/reading-content-type.enum';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TtsHelperService} from '@shared/utils/tts-helper.service';
import {TtsRestService} from '@core/modules/rest/text-to-speech/tts-rest.service';

@UntilDestroy()
@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'app-submit-dialog',
    templateUrl: './submit-dialog.component.html',
    styleUrls: ['./submit-dialog.component.scss']
})
export class SubmitDialogComponent implements OnInit {

    @Output() submitIndication: EventEmitter<FinishReadingDialogEnum> = new EventEmitter();
    @Output() logoutEvent: EventEmitter<void> = new EventEmitter();
    @Output() closingDialogEvent: EventEmitter<void> = new EventEmitter();

    viewIndicationCompleted: boolean;
    viewSendToPhysician: boolean;
    viewIndicationSent: boolean;
    viewRestart: boolean;
    dialogData: { title: string, description: string, logout: boolean } | undefined;

    audio = new Audio();
    firstPlay = true;
    ttsUrl: string | undefined;
    repeatButtonActive = false;
    ttsIconState = TtsControlEnum.playState;

    constructor(private dialog: MatDialogRef<SubmitDialogComponent>, @Inject(MAT_DIALOG_DATA) data: { title: string, description: string, logout: boolean },
                private ttsHelperService: TtsHelperService, private ttsService: TtsRestService) {
        this.viewIndicationCompleted = data.logout;
        this.viewSendToPhysician = false;
        this.viewIndicationSent = false;
        this.viewRestart = false;
        this.dialogData = data;
    }

    ngOnInit(): void {
        if (sessionStorage.getItem('audioMode')) {
            this.readText();
        }
    }

    close(): void {
        this.pauseAudio();
        this.dialog.close();
        this.closingDialogEvent.emit();
    }

    restartApplication(): void {
        this.save(FinishReadingDialogEnum.RESTART);
        this.close();
    }

    goToQuestionnaire(): void {
        this.save(FinishReadingDialogEnum.OPEN_FORM);
        this.close();
    }

    save(event: FinishReadingDialogEnum): void {
        this.submitIndication.emit(event);
    }

    keyUpRestartApp(event: any): void {
        if (event.key === 'Enter' || event.code === 'Space') {
            this.restartApplication();
        }
    }

    keyUpGoToQuestionnaire(event: any): void {
        if (event.key === 'Enter' || event.code === 'Space') {
            this.goToQuestionnaire();
        }
    }

    onGoToDashboard(): void {
        this.save(FinishReadingDialogEnum.DASHBOARD);
        this.close();
    }

    keyUpGoToDashBoard(event: any): void {
        if (event.key === 'Enter' || event.code === 'Space') {
            this.onGoToDashboard();
        }
    }

    readText(): void {
        if (this.firstPlay) {
            this.ttsIconState = TtsControlEnum.pauseState;
            this.convertTextToSpeech();
        } else {
            if (this.audio.ended) {
                this.playAudio();
            } else {
                if (this.ttsIconState === TtsControlEnum.playState) {
                    this.resumeAudio();
                } else {
                    this.pauseAudio();
                }
            }
        }
    }

    convertTextToSpeech(): void {
        const removedHtmlTagsTitle = this.ttsHelperService.removeHTMLTags(this.dialogData.title);
        const removedHtmlTagsInfo = this.ttsHelperService.removeSSML(this.dialogData.description);
        const finalText = removedHtmlTagsTitle.concat('? ').concat(removedHtmlTagsInfo);

        const pollyObject = {
            text: finalText,
            readingSpeed: ReadingSpeedEnum.MEDIUM,
            readingLanguage: getLanguageEnumByLanguageCode(localStorage.getItem('languageCode')),
            readingContentType: ReadingContentTypeEnum.TEXT
        };

        this.firstPlay = false;
        this.repeatButtonActive = true;
        this.ttsService.getTTS(pollyObject).pipe(untilDestroyed(this)).subscribe((data: any) => {
            this.ttsUrl = data;
            if (this.ttsUrl) {
                this.audio.src = this.ttsUrl;
                this.playAudio();
            }
        });
    }

    playAudio(): void {
        this.audio.load();
        this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
    }

    pauseAudio(): void {
        this.ttsIconState = TtsControlEnum.playState;
        if (!this.audio.ended) {
            this.audio.pause();
        }
    }

    resumeAudio(): void {
        if (!this.audio.ended) {
            if (this.ttsIconState === TtsControlEnum.pauseState) {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            } else {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            }
        }
    }

    onClose(): void {
        this.pauseAudio();
    }
}
